import _ from 'lodash';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { onError as onErrorLink } from 'apollo-link-error';

import { NetworkError } from 'app/common/fetch';
import { createErrorType } from 'app/common/error';
import {
  enableSentryRequestLogging,
  shouldLogRequestToSentry,
} from 'app/common/middlewares/sentryErrorsMiddleware';
import { shouldTrackNetworkError, trackException } from 'app/error/tracking';

const getRequestInfoForLogging = request => {
  const { variables, operationName } = request;
  // Queries that wish to send their request info to Sentry need to opt-in
  // by using the client-side only variable of `__enableSentryLogging`
  return {
    operationName,
    variables: shouldLogRequestToSentry(variables)
      ? variables
      : 'This request does not opt-in for sending `variables`.',
  };
};

// We're seeing a lot (1 every 3 minutes for 8 months on average)
// So I'm going to try to see a little more detail on why...
const formatErrorForLogging = error => {
  if (_.isString(error)) {
    return error;
  }
  if (_.has(error, 'errors')) {
    return _.map(error.errors, nestedError =>
      _.get(nestedError, 'message', nestedError)
    ).join(' ');
  }
  try {
    return _.isString(error)
      ? error
      : _.truncate(JSON.stringify(error), { length: 10000 });
  } catch (ignored) {
    // Yeah, this is weird, but I don't want to break error reporting
    return error;
  }
};

export const GraphQLError = createErrorType('GraphQLError', {
  assign(error) {
    return enableSentryRequestLogging({
      errorMessages: error.errorMessages,
      request: error.request,
      status: error.status,
      statusText: error.statusText,
      graphQLError: error.message,
    });
  },
  summary(error) {
    return `We're having some trouble connecting to one of our services. You might try reloading the page or checking your internet connection. (${error.graphQLError})`;
  },
});

const DEFAULT_ERROR_LINK_HANDLER_OPTIONS = {
  onError: trackException,
};

// Mimicking the interface of `apollo-link-error` so it's easy to migrate later
//
// operation: The Operation that errored
// graphQLErrors: An array of errors from the GraphQL endpoint
// networkError: Any error during the server response, that wasn't delivered as part of the errors field in the GraphQL result (usually happens when the server errors and doesn't return a JSON response)
const createErrorLinkHandler = ({
  onError,
} = DEFAULT_ERROR_LINK_HANDLER_OPTIONS) => ({
  operation,
  graphQLErrors,
  networkError,
}) => {
  if (networkError) {
    const {
      statusCode = '',
      result = 'Bad response or no response from GraphQL request.',
    } = networkError;

    const errorToLog = formatErrorForLogging(result);

    console.warn(`Network error: ${statusCode} ${errorToLog}`);

    if (shouldTrackNetworkError(networkError)) {
      // Throw the error, but silence it to prevent an unhelpful UX in the frontend.
      const networkErrorToThrow = new NetworkError({
        shouldGlobalizeError: false,
        request: getRequestInfoForLogging(operation),
        message: errorToLog,
      });

      onError(networkErrorToThrow);
    }
  }

  if (graphQLErrors) {
    console.warn(`GraphQL errors`, graphQLErrors);

    // Errors may come in as strings or as an object depending on the source of
    // the error... e.g. bad GQL request or missing cookie / csrf token
    const errorMessages = graphQLErrors.map(e => _.get(e, ['message'], e));

    const graphQLErrorToThrow = new GraphQLError({
      errorMessages,
      request: getRequestInfoForLogging(operation),
      message: errorMessages.join(', '),
    });

    onError(graphQLErrorToThrow);
  }
};

function createNetworkInterface({ uri, errorPolicy, opts, links }) {
  const handler = createErrorLinkHandler(errorPolicy);
  return ApolloLink.from([
    onErrorLink(handler),
    ...(links || []),
    createHttpLink({
      uri,
      ...opts,
    }),
  ]);
}

export default createNetworkInterface;
